import * as React from "react";

import {
	ChevronDown,
	ChevronUp,
	Copy,
	HelpCircle,
	Sparkles,
	Undo2,
} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { detectRedWords, hasRedWords } from "@/shared/utils/redWords";
import {
	isActivityRequired,
	isNamanHowell,
	isPregODonnell,
	isTaskRequired,
	isTaylorDay,
} from "@/shared/utils/firm";
import { useMemo, useState } from "react";

import { ABBREVIATIONS } from "@/shared/constants/abbreviations";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { FeedbackSidePanel } from "./FeedbackSidePanel";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import RedWordConfirmationDialog from "./RedWordConfirmationDialog";
import RedWordTextarea from "./RedWordTextarea";
import { SearchablePicker } from "@/shared/components/SearchablePicker";
import { cn } from "@/shared/lib/utils";
import { format } from "date-fns";
import { logger } from "@/shared/utils/logger";
import toast from "react-hot-toast";
import { useApi } from "@/shared/utils/request";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { useEffect } from "react";
import { useSettings } from "@/shared/contexts/SettingsContext";

type Matter = {
	matter: string;
	matter_id: string;
	client: string;
	description: string;
	task_group: string;
	activity_group: string;
};

/**
 * Checks if a matter belongs to Zurich (requires 0.05 unit increments instead of 0.1)
 */
function isZurichMatter(client: string): boolean {
	if (!client) return false;
	return client.toLowerCase().includes("zurich");
}

type Task = {
	task: string;
	task_description: string;
	task_group: string;
};

type Activity = {
	activity: string;
	activity_description: string;
	activity_group: string;
};

type EditEntryDialogProps = {
	onSave: (
		newEntry: {
			id: number;
			date: string;
			matter: string;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
		},
		shouldExport: boolean,
	) => void;
	onDuplicate?: (
		entry: {
			date: string;
			matter: string;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
			document_name: string | null;
			client: string | null;
			units_seconds: number;
			start_time: string | null;
			end_time: string | null;
		},
		originalId: number,
	) => Promise<void>;

	matters: Matter[];
	tasks: Task[];
	activities: Activity[];
	selectedRowData: {
		id: number;
		date: string;
		matter_name: string;
		matter_id: string;
		description: string;
		duration: number;
		task: string;
		activity: string;
		document_name?: string;
		units_seconds?: number;
		start_time?: string;
		end_time?: string;
		application?: string;
	};

	openModal: boolean;
	setOpenModal: (value: boolean) => void;
	isInsuranceDefenseModeRestricted: boolean;
	checkForRedWords: boolean;
	/** Admin feedback on narrative - shows side panel when present */
	attorneyFeedback?: string | null;
	/** Callback to resolve/clear feedback */
	onResolveFeedback?: () => Promise<void>;
};

export default function EditEntryDialog({
	onSave,
	onDuplicate,
	matters,
	tasks,
	activities,
	selectedRowData,
	openModal,
	setOpenModal,
	isInsuranceDefenseModeRestricted,
	checkForRedWords,
	attorneyFeedback,
	onResolveFeedback,
}: EditEntryDialogProps) {
	const [formValues, setFormValues] = useState({
		id: selectedRowData?.id ?? 0,
		date: selectedRowData?.date ?? "",
		matter: selectedRowData?.matter_name ?? "",
		matter_id: selectedRowData?.matter_id ?? "",
		description: selectedRowData?.description ?? "",
		duration: selectedRowData?.duration ?? 0,
		task: selectedRowData?.task ?? "",
		activity: selectedRowData?.activity ?? "",
		document_name: selectedRowData?.document_name ?? "",
	});

	const [date, setDate] = React.useState<Date>(
		formValues.date
			? new Date(
					Date.UTC(
						new Date(formValues.date).getUTCFullYear(), // Use local time parts directly
						new Date(formValues.date).getUTCMonth(),
						new Date(formValues.date).getUTCDate(),
						new Date(formValues.date).getUTCHours(),
					),
				)
			: new Date(),
	);

	const { darkMode } = useDarkMode();
	const { settings } = useSettings();

	const [emptyFields, setEmptyFields] = useState<Set<string>>(new Set());
	const [showRedWordDialog, setShowRedWordDialog] = useState(false);
	const [isFixingRedWords, setIsFixingRedWords] = useState(false);
	const [isDuplicating, setIsDuplicating] = useState(false);
	const [isResolvingFeedback, setIsResolvingFeedback] = useState(false);
	const [pendingSave, setPendingSave] = useState<{
		shouldExport: boolean;
		redWordMatches: ReturnType<typeof detectRedWords>;
	} | null>(null);

	const shouldHighlightRedWords =
		isInsuranceDefenseModeRestricted && checkForRedWords;
	const { makeApiRequest } = useApi();

	// AI Generate feature state
	const [isGeneratingWithAI, setIsGeneratingWithAI] = useState(false);
	const [previousDescription, setPreviousDescription] = useState<string | null>(
		null,
	);

	// Email "bill on date received" feature state
	const [emailReceivedDate, setEmailReceivedDate] = useState<Date | null>(null);

	// Date picker popover state (controlled to close on selection)
	const [datePopoverOpen, setDatePopoverOpen] = useState(false);
	const availableMatters = useMemo(() => {
		return matters.map((matter) => ({
			matter: matter.description,
			client: matter.client,
			name: matter.matter,
			matter_id: matter.matter_id,
			combined: `${matter.matter} | ${matter.description} - ${matter.client}`,
			combinedTrimmed: `${matter.matter.trim()} | ${matter.description.trim()} - ${matter.client.trim()}`,
		}));
	}, [matters]);

	const [searchTerm, setSearchTerm] = useState("");
	const [taskSearchTerm, setTaskSearchTerm] = useState("");
	const [activitySearchTerm, setActivitySearchTerm] = useState("");
	const filteredMatters = useMemo(() => {
		if (!searchTerm) {
			return availableMatters;
		}

		const query = searchTerm.toLowerCase();

		// Score each matter based on match quality, then limit to top 20
		return availableMatters
			.map((matter) => {
				let score = 0;
				const matterName = matter.matter.toLowerCase();
				const name = matter.name.toLowerCase();
				const client = matter.client.toLowerCase();

				// Exact match gets highest score
				if (matterName === query || name === query) score += 100;
				else if (matterName.startsWith(query) || name.startsWith(query))
					score += 50;
				else if (matterName.includes(query) || name.includes(query))
					score += 25;

				if (client === query) score += 80;
				else if (client.startsWith(query)) score += 40;
				else if (client.includes(query)) score += 20;

				return { matter, score };
			})
			.filter((item) => item.score > 0)
			.sort((a, b) => b.score - a.score)
			.slice(0, 20)
			.map((item) => item.matter);
	}, [searchTerm, availableMatters]);

	// Taylor Day default groups
	const TAYLOR_DAY_DEFAULT_TASK_GROUP = "9a69ea86-d958-482c-b2fd-35c6ee47830d";
	const TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP =
		"dca1c471-21cb-4c00-8e62-d075d854446d";

	// Naman Howell default task group
	const NAMAN_HOWELL_DEFAULT_TASK_GROUP =
		"54bc344d-0b93-93e4-c912-69a39322a816";

	const availableTasks = useMemo(() => {
		// For Taylor Day: filter tasks by the selected matter's task_group
		if (isTaylorDay()) {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);

			// Case 1: No matter selected - show default task_group
			if (!selectedMatter) {
				return tasks
					.filter((task) => task.task_group === TAYLOR_DAY_DEFAULT_TASK_GROUP)
					.map((task) => ({
						task: task.task,
						desc: task.task_description,
						task_group: task.task_group,
						combined: `${task.task} - ${task.task_description}`,
					}));
			}

			// Case 2: Matter selected but task_group is NULL/empty - return empty (task not required)
			if (
				!selectedMatter.task_group ||
				selectedMatter.task_group.trim() === ""
			) {
				return [];
			}

			// Case 3: Matter selected with valid task_group - show only that group
			return tasks
				.filter((task) => task.task_group === selectedMatter.task_group)
				.map((task) => ({
					task: task.task,
					desc: task.task_description,
					task_group: task.task_group,
					combined: `${task.task} - ${task.task_description}`,
				}));
		}

		// For Naman Howell: filter tasks by task_group, not required when NULL
		if (isNamanHowell()) {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);

			// No matter selected - show default task group
			if (!selectedMatter) {
				return tasks
					.filter((task) => task.task_group === NAMAN_HOWELL_DEFAULT_TASK_GROUP)
					.map((task) => ({
						task: task.task,
						desc: task.task_description,
						task_group: task.task_group,
						combined: `${task.task} - ${task.task_description}`,
					}));
			}

			// Matter selected but task_group is NULL - not required, return empty
			if (
				!selectedMatter.task_group ||
				selectedMatter.task_group.trim() === ""
			) {
				return [];
			}

			// Matter selected with valid task_group - show only that group
			return tasks
				.filter((task) => task.task_group === selectedMatter.task_group)
				.map((task) => ({
					task: task.task,
					desc: task.task_description,
					task_group: task.task_group,
					combined: `${task.task} - ${task.task_description}`,
				}));
		}

		// Default: show all tasks for other firms
		return tasks.map((task) => ({
			task: task.task,
			desc: task.task_description,
			task_group: task.task_group,
			combined: `${task.task} - ${task.task_description}`,
		}));
	}, [tasks, matters, formValues.matter_id]);

	const filteredTasks = useMemo(() => {
		// Deduplicate tasks by their combined display value
		const seen = new Set<string>();
		const uniqueTasks = availableTasks.filter((task) => {
			if (seen.has(task.combined)) return false;
			seen.add(task.combined);
			return true;
		});

		// For Naman Howell: sort L codes first, E codes last
		if (isNamanHowell()) {
			uniqueTasks.sort((a, b) => {
				const aIsL = a.task.startsWith("L");
				const bIsL = b.task.startsWith("L");
				const aIsE = a.task.startsWith("E");
				const bIsE = b.task.startsWith("E");
				if (aIsL && !bIsL) return -1;
				if (!aIsL && bIsL) return 1;
				if (aIsE && !bIsE) return 1;
				if (!aIsE && bIsE) return -1;
				return 0;
			});
		}

		if (!taskSearchTerm) {
			return uniqueTasks;
		}
		return uniqueTasks.filter(
			(task) =>
				task.task.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
				task.desc.toLowerCase().includes(taskSearchTerm.toLowerCase()),
		);
	}, [taskSearchTerm, availableTasks]);

	// For Naman Howell: hide E codes by default, show them only when searching
	const taskMaxItems = useMemo(() => {
		if (!isNamanHowell()) return 100;
		return filteredTasks.filter((t) => !t.task.startsWith("E")).length || 100;
	}, [filteredTasks]);

	const availableActivities = useMemo(() => {
		// For Preg O'Donnell: filter activities by the selected matter's activity_group
		if (isPregODonnell()) {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);

			// Case 1: No matter selected - show "ABA" group as default
			if (!selectedMatter) {
				return activities
					.filter((activity) => activity.activity_group === "ABA")
					.map((activity) => ({
						activity: activity.activity,
						desc: activity.activity_description,
						activity_group: activity.activity_group,
						combined: `${activity.activity} - ${activity.activity_description}`,
					}));
			}

			// Case 2: Matter selected but activity_group is NULL/empty - return empty (no activity codes)
			if (
				!selectedMatter.activity_group ||
				selectedMatter.activity_group.trim() === ""
			) {
				return [];
			}

			// Case 3: Matter selected with valid activity_group - show only that group
			return activities
				.filter(
					(activity) =>
						activity.activity_group === selectedMatter.activity_group,
				)
				.map((activity) => ({
					activity: activity.activity,
					desc: activity.activity_description,
					activity_group: activity.activity_group,
					combined: `${activity.activity} - ${activity.activity_description}`,
				}));
		}

		// For Taylor Day: filter activities by the selected matter's activity_group
		if (isTaylorDay()) {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);

			// Case 1: No matter selected - show default activity_group
			if (!selectedMatter) {
				return activities
					.filter(
						(activity) =>
							activity.activity_group === TAYLOR_DAY_DEFAULT_ACTIVITY_GROUP,
					)
					.map((activity) => ({
						activity: activity.activity,
						desc: activity.activity_description,
						activity_group: activity.activity_group,
						combined: `${activity.activity} - ${activity.activity_description}`,
					}));
			}

			// Case 2: Matter selected but activity_group is NULL/empty - return empty (activity not required)
			if (
				!selectedMatter.activity_group ||
				selectedMatter.activity_group.trim() === ""
			) {
				return [];
			}

			// Case 3: Matter selected with valid activity_group - show only that group
			return activities
				.filter(
					(activity) =>
						activity.activity_group === selectedMatter.activity_group,
				)
				.map((activity) => ({
					activity: activity.activity,
					desc: activity.activity_description,
					activity_group: activity.activity_group,
					combined: `${activity.activity} - ${activity.activity_description}`,
				}));
		}

		// For Naman Howell: activity is always available (optional or required based on task_group)
		if (isNamanHowell()) {
			const selectedMatter = matters.find(
				(matter) => matter.matter_id === formValues.matter_id,
			);

			// Filter by activity_group if matter has one, otherwise show all
			if (
				selectedMatter?.activity_group &&
				selectedMatter.activity_group.trim() !== ""
			) {
				return activities
					.filter(
						(activity) =>
							activity.activity_group === selectedMatter.activity_group,
					)
					.map((activity) => ({
						activity: activity.activity,
						desc: activity.activity_description,
						activity_group: activity.activity_group,
						combined: `${activity.activity} - ${activity.activity_description}`,
					}));
			}

			// No matter or no activity_group - show all activities
			return activities.map((activity) => ({
				activity: activity.activity,
				desc: activity.activity_description,
				activity_group: activity.activity_group,
				combined: `${activity.activity} - ${activity.activity_description}`,
			}));
		}

		// Default: show all activities for other firms
		return activities.map((activity) => ({
			activity: activity.activity,
			desc: activity.activity_description,
			activity_group: activity.activity_group,
			combined: `${activity.activity} - ${activity.activity_description}`,
		}));
	}, [activities, matters, formValues.matter_id]);

	const filteredActivities = useMemo(() => {
		if (!activitySearchTerm) {
			return availableActivities;
		}

		// Search within availableActivities only (no cross-group searching)
		return availableActivities.filter(
			(activity) =>
				activity.activity
					.toLowerCase()
					.includes(activitySearchTerm.toLowerCase()) ||
				activity.desc.toLowerCase().includes(activitySearchTerm.toLowerCase()),
		);
	}, [activitySearchTerm, availableActivities]);

	// Determine if activity is required for the currently selected matter
	const activityRequiredForCurrentMatter = useMemo(() => {
		return isActivityRequired(formValues.matter_id, matters);
	}, [formValues.matter_id, matters]);

	// Determine if task is required for the currently selected matter
	const taskRequiredForCurrentMatter = useMemo(() => {
		return isTaskRequired(formValues.matter_id, matters);
	}, [formValues.matter_id, matters]);

	const handleMatterChange = (value: string) => {
		const selectedMatter = availableMatters.find(
			(matter) => matter.matter_id === value,
		);

		if (!selectedMatter) {
			// If no matter is found, clear both fields
			setFormValues((prev) => {
				return { ...prev, matter: "", matter_id: "" };
			});
			setEmptyFields((prev) => {
				const newSet = new Set(prev);
				newSet.add("matter");
				return newSet;
			});
			return false; // Return false to indicate failure
		}

		const matterValue = selectedMatter.name;
		const matterIdValue = selectedMatter.matter_id;

		// Check if the new matter requires activity codes
		const newMatterRequiresActivity = isActivityRequired(
			matterIdValue,
			matters,
		);

		// If the new matter doesn't require activity, clear any existing activity selection
		const shouldClearActivity = !newMatterRequiresActivity;

		// Check if the new matter requires task codes (Taylor Day only)
		const newMatterRequiresTask = isTaskRequired(matterIdValue, matters);

		// If the new matter doesn't require task, clear any existing task selection
		const shouldClearTask = !newMatterRequiresTask;

		// Determine unit increment for new matter and adjust units if needed
		const newIsZurich = isZurichMatter(selectedMatter.client);
		const newStep = newIsZurich ? 0.05 : 0.1;

		// Check if the previous matter was Zurich
		const prevMatter = matters.find(
			(m) => m.matter_id === formValues.matter_id,
		);
		const prevIsZurich = isZurichMatter(prevMatter?.client || "");

		setFormValues((prev) => {
			// Adjust duration to match new matter's increment
			let adjustedDuration = prev.duration;

			if (typeof prev.duration === "number" && prev.duration > 0) {
				// Special case: if switching from non-Zurich minimum (0.1) to Zurich,
				// use Zurich minimum (0.05) - "minimum should stay minimum"
				if (
					!prevIsZurich &&
					newIsZurich &&
					Math.abs(prev.duration - 0.1) < 0.001
				) {
					adjustedDuration = 0.05;
				} else {
					// Always round UP to next increment (0.05 -> 0.1, 1.25 -> 1.3)
					// First fix floating-point precision on the ratio, then ceil
					const ratio = Math.round((prev.duration / newStep) * 100) / 100;
					adjustedDuration = Math.ceil(ratio) * newStep;
					// Ensure minimum
					if (adjustedDuration < newStep) {
						adjustedDuration = newStep;
					}
					// Fix floating point precision (e.g., 0.30000000000000004 -> 0.3)
					adjustedDuration = Math.round(adjustedDuration * 100) / 100;
				}
			}

			return {
				...prev,
				matter: matterValue,
				matter_id: matterIdValue,
				duration: adjustedDuration,
				...(shouldClearActivity ? { activity: "" } : {}),
				...(shouldClearTask ? { task: "" } : {}),
			};
		});

		// Clear the activity display value if matter doesn't require activity
		if (shouldClearActivity) {
			setActivityValue("");
		}

		// Clear the task display value if matter doesn't require task
		if (shouldClearTask) {
			setTaskValue("");
		}

		setEmptyFields((prev) => {
			const newSet = new Set(prev);
			if (matterValue.trim() !== "" && matterIdValue.trim() !== "") {
				newSet.delete("matter");
			} else {
				newSet.add("matter");
			}
			// Remove activity from empty fields if it's not required for this matter
			if (shouldClearActivity) {
				newSet.delete("activity");
			}
			// Remove task from empty fields if it's not required for this matter
			if (shouldClearTask) {
				newSet.delete("task");
			}
			return newSet;
		});

		return true; // Return true to indicate success
	};

	const [value, setValue] = React.useState("");
	const [taskValue, setTaskValue] = React.useState("");
	const [activityValue, setActivityValue] = React.useState("");

	const handleTaskChange = (value: string) => {
		const taskValue = availableTasks.find(
			(task) => task.combined === value,
		)!.task;

		if (taskValue === formValues.task) {
			setFormValues((prev) => {
				return { ...prev, task: "" };
			});
			return;
		}

		setFormValues((prev) => {
			return { ...prev, task: taskValue };
		});
		setEmptyFields((prev) => {
			const newSet = new Set(prev);
			if (value.trim() !== "") {
				newSet.delete("task");
			} else {
				newSet.add("task");
			}
			return newSet;
		});
	};

	const handleActivityChange = (value: string) => {
		const activityValue = availableActivities.find(
			(activity) => activity.combined === value,
		)!.activity;

		if (activityValue === formValues.activity) {
			setFormValues((prev) => {
				return { ...prev, activity: "" };
			});
			return;
		}

		setFormValues((prev) => {
			return { ...prev, activity: activityValue };
		});
		setEmptyFields((prev) => {
			const newSet = new Set(prev);
			if (value.trim() !== "") {
				newSet.delete("activity");
			} else {
				newSet.add("activity");
			}
			return newSet;
		});
	};

	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	const handleDescriptionChange = (
		e: React.ChangeEvent<HTMLTextAreaElement>,
	) => {
		const textarea = textareaRef.current;
		if (!textarea) return;

		let { value } = e.target;

		const cursorPos = textarea.selectionStart;
		const beforeCursor = value.slice(0, cursorPos);
		const afterCursor = value.slice(cursorPos);

		const match = beforeCursor.match(/(\b\w+)\s$/);

		if (match && settings.abbreviations_enabled) {
			const abbreviation = match[1].toLowerCase();
			const replacement = ABBREVIATIONS[abbreviation];

			if (replacement) {
				const startOfWord = beforeCursor.lastIndexOf(match[1]);
				const newBeforeCursor =
					beforeCursor.slice(0, startOfWord) + replacement + " ";

				const newValue = newBeforeCursor + afterCursor;

				setFormValues((prev) => ({
					...prev,
					description: newValue,
				}));

				requestAnimationFrame(() => {
					const newCursorPos = newBeforeCursor.length;
					textarea.setSelectionRange(newCursorPos, newCursorPos);
				});

				return;
			}
		}

		setFormValues((prev) => ({
			...prev,
			description: value,
		}));
	};

	const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		// Store raw string while typing to allow "0.", "0.0", "0.05" etc.
		// Parsing/rounding happens on blur, not during typing
		setFormValues((prev) => ({ ...prev, duration: value as any }));

		setEmptyFields((prev) => {
			const newSet = new Set(prev);
			if (value.trim() !== "") {
				newSet.delete("duration");
			} else {
				newSet.add("duration");
			}
			return newSet;
		});
	};

	const handleDurationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const rawValue = parseFloat(e.target.value);
		const selectedMatter = matters.find(
			(matter) => matter.matter_id === formValues.matter_id,
		);
		const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

		// If invalid or empty, restore to original value (rounded UP to current matter's step)
		if (isNaN(rawValue) || e.target.value === "" || rawValue <= 0) {
			const original = selectedRowData?.duration || step;
			// Round original UP to current matter's step in case matter changed
			const ratio = Math.round((original / step) * 100) / 100;
			let fallback = Math.ceil(ratio) * step;
			fallback = Math.round(fallback * 100) / 100;
			if (fallback < step) fallback = step;
			setFormValues((prev) => ({ ...prev, duration: fallback }));
			return;
		}

		// Round UP to next valid increment (0.05 -> 0.1, 1.25 -> 1.3)
		const ratio = Math.round((rawValue / step) * 100) / 100;
		let rounded = Math.ceil(ratio) * step;
		rounded = Math.round(rounded * 100) / 100; // Fix precision (e.g., 0.30000000004 -> 0.3)

		// Ensure minimum
		if (rounded < step) {
			rounded = step;
		}

		setFormValues((prev) => ({ ...prev, duration: rounded }));
	};

	const handleDurationIncrement = () => {
		const selectedMatter = matters.find(
			(matter) => matter.matter_id === formValues.matter_id,
		);
		const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

		const currentValue =
			typeof formValues.duration === "number"
				? formValues.duration
				: parseFloat(String(formValues.duration)) || 0;

		const newValue = Math.round((currentValue + step) * 100) / 100;
		setFormValues((prev) => ({ ...prev, duration: newValue }));
	};

	const handleDurationDecrement = () => {
		const selectedMatter = matters.find(
			(matter) => matter.matter_id === formValues.matter_id,
		);
		const step = isZurichMatter(selectedMatter?.client || "") ? 0.05 : 0.1;

		const currentValue =
			typeof formValues.duration === "number"
				? formValues.duration
				: parseFloat(String(formValues.duration)) || 0;

		const newValue = Math.max(
			step,
			Math.round((currentValue - step) * 100) / 100,
		);
		setFormValues((prev) => ({ ...prev, duration: newValue }));
	};

	const handleDateChange = (date: Date | undefined) => {
		if (!date) return;

		setDate(date as Date);
		setDatePopoverOpen(false); // Close popover on date selection

		setFormValues((prev) => {
			return { ...prev, date: format(date!, "MM/dd/yyyy") };
		});
		setEmptyFields((prev) => {
			const newSet = new Set(prev);
			if (date) {
				newSet.delete("date");
			} else {
				newSet.add("date");
			}
			return newSet;
		});
	};

	// Track whether we've initialized for the current selectedRowData
	const initializedForEntryRef = React.useRef<number | null>(null);

	useEffect(() => {
		// Only initialize when selectedRowData changes to a NEW entry
		// Don't re-run when availableActivities/availableTasks change due to matter selection
		if (
			selectedRowData &&
			initializedForEntryRef.current !== selectedRowData.id
		) {
			initializedForEntryRef.current = selectedRowData.id;

			const newFormValues = {
				...selectedRowData,
				date: selectedRowData.date || "",
				matter: selectedRowData.matter_name || "",
				matter_id: selectedRowData.matter_id || "",
				duration: selectedRowData.duration || 0,
				description: selectedRowData.description || "",
				task: selectedRowData.task || "",
				activity: selectedRowData.activity || "",
				document_name: selectedRowData.document_name || "",
			};
			setFormValues(newFormValues);
			setValue(selectedRowData.matter_id || "");

			// Find the matching task and use its combined value for the picker
			const matchingTask = tasks.find((t) => t.task === selectedRowData.task);
			setTaskValue(
				matchingTask
					? `${matchingTask.task} - ${matchingTask.task_description}`
					: "",
			);

			// Find the matching activity and use its combined value for the picker
			const matchingActivity = activities.find(
				(a) => a.activity === selectedRowData.activity,
			);
			setActivityValue(
				matchingActivity
					? `${matchingActivity.activity} - ${matchingActivity.activity_description}`
					: "",
			);

			setDate(new Date(selectedRowData.date));
			setPreviousDescription(null); // Clear undo state when switching entries

			// Reset search terms so filtered lists include the current selections
			setSearchTerm("");
			setTaskSearchTerm("");
			setActivitySearchTerm("");

			// Check for empty fields when setting initial values
			const newEmptyFields = new Set<string>();
			Object.entries(newFormValues).forEach(([key, value]) => {
				if (
					value === "" ||
					value === 0 ||
					value === "Please fill in the narrative."
				) {
					newEmptyFields.add(key);
				}
			});
			setEmptyFields(newEmptyFields);
		}
	}, [selectedRowData, tasks, activities]);

	// Reset state when modal closes for clean slate on next open
	useEffect(() => {
		if (!openModal) {
			initializedForEntryRef.current = null;
			setPreviousDescription(null);
			// Reset search terms so next open shows full lists
			setSearchTerm("");
			setTaskSearchTerm("");
			setActivitySearchTerm("");
			setEmailReceivedDate(null);
		}
	}, [openModal]);

	// Fetch email received date when dialog opens for email entries
	useEffect(() => {
		if (!openModal || !selectedRowData?.id) {
			return;
		}

		// Only fetch for Outlook (email) entries
		if (selectedRowData?.application !== "Microsoft Outlook") {
			setEmailReceivedDate(null);
			return;
		}

		const fetchEmailReceivedDate = async () => {
			try {
				const response = await makeApiRequest({
					endpoint: `/records/${selectedRowData.id}/email-received-date`,
					method: "GET",
				});

				if (response.data?.receivedDate) {
					const receivedDate = new Date(response.data.receivedDate);
					// Only set if different from current date (user's requirement #3)
					if (receivedDate.toDateString() !== date.toDateString()) {
						setEmailReceivedDate(receivedDate);
					} else {
						setEmailReceivedDate(null);
					}
				}
			} catch (error) {
				// Silent fail - feature degrades gracefully (no highlight shown)
				logger.warn("Could not fetch email received date", {
					entryId: selectedRowData.id,
					error: error instanceof Error ? error.message : String(error),
				});
			}
		};

		fetchEmailReceivedDate();
	}, [openModal, selectedRowData?.id, selectedRowData?.application]);

	const handleFixRedWords = async (originalDescription: string) => {
		try {
			setIsFixingRedWords(true);

			const response = await makeApiRequest({
				endpoint: "/records/redWords",
				method: "POST",
				body: { description: originalDescription, date: formValues.date },
			});

			if (response.error) {
				throw new Error(response.error);
			}

			if (!response.data) {
				throw new Error("Invalid response format from red words API");
			}

			const fixedDescription = response.data;

			// Update the form with the fixed description
			if (typeof fixedDescription === "string" && fixedDescription.trim()) {
				setFormValues((prev) => ({
					...prev,
					description: fixedDescription,
				}));
			} else {
				throw new Error("Invalid fixed description received");
			}

			// Close the dialog and clear pending save
			setShowRedWordDialog(false);
			setPendingSave(null);

			toast.success("Red words have been automatically replaced!", {
				position: "top-center",
				duration: 3000,
			} as any);
		} catch (error) {
			logger.error("Red words fix failed", {
				error: error instanceof Error ? error.message : String(error),
				entryId: formValues.id,
			});
			if (error instanceof Error) {
				logger.captureException(error, {
					action: "fixRedWords",
					entryId: formValues.id,
				});
			}
			toast.error("Failed to fix red words. Please try manually.", {
				position: "top-center",
				duration: 3000,
			} as any);
		} finally {
			setIsFixingRedWords(false);
		}
	};

	const handleGenerateWithAI = async () => {
		// Validate minimum length (20 chars) - matches addDialog behavior
		if (formValues.description.trim().length < 20) {
			toast.error(
				"Please enter a longer description (at least 20 characters).",
				{
					position: "top-center",
					duration: 2500,
				} as any,
			);
			return;
		}

		// Capture entry ID to guard against entry switching during async operation
		const entryIdAtStart = formValues.id;

		// Save current description for undo
		const descriptionBeforeEdit = formValues.description;
		setIsGeneratingWithAI(true);

		try {
			const editedDescription = await logger.trackOperation(
				"AI narrative generation",
				async () => {
					const response = await makeApiRequest({
						endpoint: "/records/generateEditedDescription",
						method: "POST",
						body: {
							id: formValues.id,
							description: formValues.description,
							// No prompt - backend will use default
						},
					});

					if (response.error) {
						throw new Error(response.error);
					}

					if (
						!response.data ||
						typeof response.data !== "string" ||
						!response.data.trim()
					) {
						throw new Error("Invalid response from AI generation");
					}

					return response.data;
				},
				{ inputLength: formValues.description.length },
				3000,
			);

			// Guard: if user switched to a different entry, discard result silently
			if (formValues.id !== entryIdAtStart) {
				console.log(
					"[AI Generate] Entry changed during request, discarding result",
				);
				return;
			}

			// Strip surrounding quotes if LLM wrapped the response
			let cleanedDescription = editedDescription
				.trim()
				.replace(/^["']|["']$/g, "");

			// Debug: log what we received
			console.log("[AI Generate] Response received:", {
				original: editedDescription,
				cleaned: cleanedDescription,
				hadQuotes: editedDescription !== cleanedDescription,
			});

			// Validate we have actual content
			if (!cleanedDescription || cleanedDescription.trim() === "") {
				throw new Error("AI returned empty response");
			}

			// Store previous description for undo functionality
			setPreviousDescription(descriptionBeforeEdit);

			// Update form state
			setFormValues((prev) => ({
				...prev,
				description: cleanedDescription,
			}));

			// Also set via ref as fallback (in case React state doesn't update properly)
			if (textareaRef.current) {
				textareaRef.current.value = cleanedDescription;
			}

			toast.success("Narrative generated with AI!", {
				position: "top-center",
				duration: 3000,
			} as any);
		} catch (error) {
			if (error instanceof Error) {
				logger.captureException(error, {
					action: "generateWithAI",
					entryId: formValues.id,
				});
			}
			toast.error("Failed to generate with AI. Please try manually.", {
				position: "top-center",
				duration: 3000,
			} as any);
		} finally {
			setIsGeneratingWithAI(false);
		}
	};

	const handleUndoAIEdit = () => {
		if (previousDescription !== null) {
			setFormValues((prev) => ({
				...prev,
				description: previousDescription,
			}));
			setPreviousDescription(null);
			toast.success("Reverted to previous narrative", {
				position: "top-center",
				duration: 2000,
			} as any);
		}
	};

	const handleConfirmWithRedWords = () => {
		if (pendingSave) {
			const { shouldExport } = pendingSave;

			setValue("");
			setTaskValue("");
			setActivityValue("");

			onSave(formValues, shouldExport);
			setOpenModal(false);
			setFormValues({
				id: 0,
				date: "",
				matter: "",
				matter_id: "",
				description: "",
				duration: 0,
				activity: "",
				task: "",
				document_name: "",
			});
			setEmptyFields(new Set());
			setShowRedWordDialog(false);
			setPendingSave(null);
		}
	};

	const validateAndSave = (shouldExport: boolean) => {
		const { matter, matter_id, description, duration, task, activity, date } =
			formValues;

		// Use the cached useMemo values for consistency with the UI
		// This ensures validation matches what the user sees (disabled fields, "Not required" labels)
		const taskRequired = taskRequiredForCurrentMatter;
		const activityRequired = activityRequiredForCurrentMatter;

		if (
			!date ||
			!matter ||
			!matter_id ||
			!description ||
			!duration ||
			(taskRequired && !task) ||
			(activityRequired && !activity) ||
			description === "Please fill in the narrative."
		) {
			// Log validation failure with form state to debug user-reported issues
			logger.warn("Edit entry validation failed - missing fields", {
				action: "editEntry",
				hasDate: !!date,
				hasMatter: !!matter,
				hasMatterId: !!matter_id,
				hasDescription:
					!!description && description !== "Please fill in the narrative.",
				hasDuration: !!duration,
				hasTask: !!task,
				taskRequired,
				hasActivity: !!activity,
				activityRequired,
				entryId: formValues.id,
			});
			toast.error("Please fill out all fields", {
				position: "top-center",
				duration: 2500,
			});
			return;
		}
		const today = new Date();
		const selectedDate = new Date(formValues.date);

		if (!selectedDate) {
			logger.warn("Edit entry validation failed - invalid date", {
				action: "editEntry",
				dateValue: formValues.date,
				entryId: formValues.id,
			});
			toast.error("Please select a date");
			return;
		}

		if (selectedDate > today) {
			logger.warn("Edit entry validation failed - future date", {
				action: "editEntry",
				selectedDate: formValues.date,
				entryId: formValues.id,
			});
			toast.error("Record date cannot be in the future");
			return;
		}

		if (duration < 0) {
			logger.warn("Edit entry validation failed - invalid duration", {
				action: "editEntry",
				duration,
				entryId: formValues.id,
			});
			toast.error("Please enter valid time", {
				position: "top-center",
				duration: 2500,
			});
			return;
		}

		// Validate units match required increment (0.05 for Zurich, 0.1 for others)
		const selectedMatter = matters.find((m) => m.matter_id === matter_id);
		const requiredStep = isZurichMatter(selectedMatter?.client || "")
			? 0.05
			: 0.1;
		const remainder = Math.round((duration % requiredStep) * 1000) / 1000; // Fix floating point

		if (remainder !== 0 && Math.abs(remainder - requiredStep) > 0.001) {
			logger.warn("Edit entry validation failed - invalid unit increment", {
				action: "editEntry",
				duration,
				requiredStep,
				remainder,
				entryId: formValues.id,
			});
			toast.error(`Units must be in increments of ${requiredStep}`, {
				position: "top-center",
				duration: 2500,
			});
			return;
		}

		// Check for red words if restrictions apply
		if (shouldHighlightRedWords && hasRedWords(description)) {
			const redWordMatches = detectRedWords(description);
			setPendingSave({ shouldExport, redWordMatches });
			setShowRedWordDialog(true);
			return;
		}

		setValue("");
		setTaskValue("");
		setActivityValue("");

		// Clear activity/task codes if not required for this matter (sends null to backend)
		// For Naman Howell: preserve activity even when optional (user may have selected one)
		let finalFormValues = formValues;
		if (!activityRequired && !isNamanHowell()) {
			finalFormValues = { ...finalFormValues, activity: "" };
		}
		if (!taskRequired) {
			finalFormValues = { ...finalFormValues, task: "" };
		}

		onSave(finalFormValues, shouldExport);
		setOpenModal(false);
		setFormValues({
			id: 0,
			date: "",
			matter: "",
			matter_id: "",
			description: "",
			duration: 0,
			activity: "",
			task: "",
			document_name: "",
		});
		setEmptyFields(new Set());
	};

	const handleDuplicate = async () => {
		if (!onDuplicate) return;

		const { matter, matter_id, description, duration, task, activity, date } =
			formValues;

		// Use the cached useMemo values for consistency with the UI
		const taskRequired = taskRequiredForCurrentMatter;
		const activityRequired = activityRequiredForCurrentMatter;

		// Validate all required fields (same as save validation)
		if (
			!date ||
			!matter ||
			!matter_id ||
			!description ||
			!duration ||
			(taskRequired && !task) ||
			(activityRequired && !activity) ||
			description === "Please fill in the narrative."
		) {
			logger.warn("Duplicate entry validation failed - missing fields", {
				action: "duplicateEntry",
				hasDate: !!date,
				hasMatter: !!matter,
				hasMatterId: !!matter_id,
				hasDescription:
					!!description && description !== "Please fill in the narrative.",
				hasDuration: !!duration,
				hasTask: !!task,
				taskRequired,
				hasActivity: !!activity,
				activityRequired,
				entryId: formValues.id,
			});
			toast.error("Please fill out all fields before duplicating", {
				position: "top-center",
				duration: 2500,
			});
			return;
		}

		const today = new Date();
		const selectedDate = new Date(formValues.date);

		if (!selectedDate) {
			logger.warn("Duplicate entry validation failed - invalid date", {
				action: "duplicateEntry",
				dateValue: formValues.date,
				entryId: formValues.id,
			});
			toast.error("Please select a date");
			return;
		}

		if (selectedDate > today) {
			logger.warn("Duplicate entry validation failed - future date", {
				action: "duplicateEntry",
				selectedDate: formValues.date,
				entryId: formValues.id,
			});
			toast.error("Record date cannot be in the future");
			return;
		}

		if (duration < 0) {
			logger.warn("Duplicate entry validation failed - invalid duration", {
				action: "duplicateEntry",
				duration,
				entryId: formValues.id,
			});
			toast.error("Please enter valid time", {
				position: "top-center",
				duration: 2500,
			});
			return;
		}

		// Validate units match required increment (0.05 for Zurich, 0.1 for others)
		const selectedMatter = matters.find((m) => m.matter_id === matter_id);
		const requiredStep = isZurichMatter(selectedMatter?.client || "")
			? 0.05
			: 0.1;
		const remainder = Math.round((duration % requiredStep) * 1000) / 1000;

		if (remainder !== 0 && Math.abs(remainder - requiredStep) > 0.001) {
			logger.warn(
				"Duplicate entry validation failed - invalid unit increment",
				{
					action: "duplicateEntry",
					duration,
					requiredStep,
					remainder,
					entryId: formValues.id,
				},
			);
			toast.error(`Units must be in increments of ${requiredStep}`, {
				position: "top-center",
				duration: 2500,
			});
			return;
		}

		// Prepare entry for duplication
		// Clear task/activity if not required for this matter
		// Include timing fields from the original entry
		const entryToDuplicate = {
			date: formValues.date,
			matter: formValues.matter,
			matter_id: formValues.matter_id,
			description: formValues.description,
			duration: formValues.duration,
			task: taskRequired ? formValues.task : "",
			activity: (activityRequired || isNamanHowell()) ? formValues.activity : "",
			document_name: formValues.document_name || null,
			client: selectedMatter?.client || null,
			units_seconds: selectedRowData.units_seconds || 0,
			start_time: selectedRowData.start_time || null,
			end_time: selectedRowData.end_time || null,
		};

		setIsDuplicating(true);
		try {
			await onDuplicate(entryToDuplicate, formValues.id);
			toast.success("Entry duplicated", {
				position: "top-center",
				duration: 2500,
			});
		} catch (e) {
			if (e instanceof Error) {
				logger.captureException(e, {
					action: "duplicateEntry",
					entryId: formValues.id,
				});
			}
			toast.error("Error duplicating entry", {
				position: "top-center",
				duration: 2500,
			});
		} finally {
			setIsDuplicating(false);
		}
	};

	return (
		<Dialog open={openModal} onOpenChange={setOpenModal}>
			{/* <DialogTrigger asChild>
        <Button variant="outline" onClick={() => setOpen(true)}>
          Edit entry
        </Button>
      </DialogTrigger> */}
			<DialogContent
				className={cn(
					"sm:max-w-[525px]",
					darkMode ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]" : "",
					attorneyFeedback && "overflow-visible", // Allow side panel to overflow
				)}
				style={
					attorneyFeedback
						? { transform: "translate(calc(-50% - 150px), -50%)" }
						: undefined
				}
			>
				{/* Feedback side panel - appears to the right when feedback exists */}
				{attorneyFeedback && (
					<FeedbackSidePanel
						feedback={attorneyFeedback}
						onResolve={async () => {
							setIsResolvingFeedback(true);
							try {
								await onResolveFeedback?.();
							} finally {
								setIsResolvingFeedback(false);
							}
						}}
						isResolving={isResolvingFeedback}
					/>
				)}
				<DialogHeader>
					<DialogTitle>Edit entry</DialogTitle>
					<DialogDescription>Manually edit an entry here.</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					{formValues.document_name &&
						formValues.document_name.trim() !== "" && (
							<div className="grid w-full gap-1.5">
								<Label htmlFor="document-name">Task Title</Label>
								<Input
									id="document-name"
									value={formValues.document_name}
									className={cn(
										"col-span-3",
										darkMode
											? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
											: "",
									)}
									readOnly
									disabled
								/>
							</div>
						)}
					<div className="grid w-full gap-1.5">
						<Label htmlFor="username">Matter</Label>
						<SearchablePicker
							items={filteredMatters}
							value={value}
							onChange={(matterId) => {
								const success = handleMatterChange(matterId);
								if (success) {
									setValue(matterId);
								}
							}}
							getValue={(m) => m.matter_id}
							getLabel={(m) => m.combinedTrimmed}
							placeholder="Select matter..."
							searchPlaceholder="Search matter..."
							emptyMessage="No matter found."
							searchTerm={searchTerm}
							onSearchChange={setSearchTerm}
							error={emptyFields.has("matter")}
						/>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="username">Task</Label>
							<SearchablePicker
								items={filteredTasks}
								value={taskValue}
								onChange={(newValue) => {
									setTaskValue(newValue);
									handleTaskChange(newValue);
								}}
								getValue={(t) => t.combined}
								getLabel={(t) => `${t.task} - ${t.desc}`}
								placeholder="Select task code..."
								searchPlaceholder="Search task..."
								emptyMessage="No task found."
								disabled={!taskRequiredForCurrentMatter}
								disabledMessage="Not required"
								popoverWidth="280px"
								searchTerm={taskSearchTerm}
								onSearchChange={setTaskSearchTerm}
								maxItemsBeforeSearch={taskMaxItems}
								error={taskRequiredForCurrentMatter && emptyFields.has("task")}
							/>
						</div>
						<div className="grid w-full gap-1.5">
							<Label htmlFor="username">Activity</Label>
							<SearchablePicker
								items={filteredActivities}
								value={activityValue}
								onChange={(newValue) => {
									setActivityValue(newValue);
									handleActivityChange(newValue);
								}}
								getValue={(a) => a.combined}
								getLabel={(a) => `${a.activity} - ${a.desc}`}
								placeholder="Select activity code..."
								searchPlaceholder="Search activity..."
								emptyMessage="No activity found."
								disabled={!activityRequiredForCurrentMatter && !isNamanHowell()}
								disabledMessage="Not required"
								popoverWidth="280px"
								searchTerm={activitySearchTerm}
								onSearchChange={setActivitySearchTerm}
								maxItemsBeforeSearch={100}
								error={
									activityRequiredForCurrentMatter &&
									emptyFields.has("activity")
								}
								clearable={!activityRequiredForCurrentMatter && isNamanHowell()}
								onClear={() => {
									setActivityValue("");
									setFormValues((prev) => ({ ...prev, activity: "" }));
								}}
							/>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="date">Date</Label>
							<Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
								<PopoverTrigger asChild>
									<Button
										variant={"outline"}
										className={cn(
											"w-full justify-start text-left font-normal overflow-hidden",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]" // Dark mode hover state
												: "hover:bg-gray-100", // Light mode hover state
											!date && "text-muted-foreground",
										)}
									>
										<CalendarIcon className="mr-2 h-4 w-4" />
										{date ? format(date, "PP") : <span>Pick a date</span>}
									</Button>
								</PopoverTrigger>
								<PopoverContent className={cn("w-auto p-0")}>
									<Calendar
										mode="single"
										selected={date}
										onSelect={handleDateChange}
										initialFocus
										modifiers={{
											emailReceived: emailReceivedDate
												? [emailReceivedDate]
												: [],
										}}
										modifiersClassNames={{
											emailReceived: "day-email-received",
										}}
									/>
								</PopoverContent>
							</Popover>
						</div>
						<div className="grid w-full gap-1.5">
							<Label htmlFor="number">Units</Label>
							<div className="flex gap-1">
								<Input
									id="duration"
									value={
										!formValues.duration || formValues.duration === 0
											? ""
											: typeof formValues.duration === "number" ||
												  typeof formValues.duration === "string"
												? formValues.duration
												: ""
									}
									className={cn(
										"col-span-3",
										darkMode
											? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
											: "",
									)}
									type="text"
									inputMode="decimal"
									autoComplete="off"
									onChange={handleDurationChange}
									onBlur={handleDurationBlur}
								/>
								<div className="flex flex-col gap-0.5">
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleDurationIncrement}
										className={cn(
											"h-[18px] w-7 p-0",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
												: "hover:bg-gray-100",
										)}
									>
										<ChevronUp className="h-3 w-3" />
									</Button>
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleDurationDecrement}
										className={cn(
											"h-[18px] w-7 p-0",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
												: "hover:bg-gray-100",
										)}
									>
										<ChevronDown className="h-3 w-3" />
									</Button>
								</div>
							</div>
						</div>
					</div>
					<div className="grid w-full gap-1.5">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-1.5">
								<Label htmlFor="message">Narrative</Label>
								{settings.abbreviations_enabled && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<button
													type="button"
													className="text-muted-foreground hover:text-foreground"
													aria-label="Abbreviation help"
												>
													<HelpCircle className="h-4 w-4" />
												</button>
											</TooltipTrigger>
											<TooltipContent
												side="right"
												className="px-2 py-1 text-xs"
											>
												<p>Abbreviations enabled</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								)}
							</div>
							<div className="flex items-center gap-2">
								{previousDescription !== null && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={handleUndoAIEdit}
										className={cn(
											"flex items-center gap-1 h-8 text-xs",
											darkMode
												? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
												: "hover:bg-gray-100",
										)}
									>
										<Undo2 className="h-3 w-3" />
										Undo
									</Button>
								)}
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleGenerateWithAI}
									disabled={
										isGeneratingWithAI || !formValues.description.trim()
									}
									className={cn(
										"flex items-center gap-2 h-8 text-xs",
										darkMode
											? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
											: "hover:bg-gray-100",
									)}
								>
									<Sparkles className="h-3 w-3" />
									{isGeneratingWithAI ? "Generating..." : "Generate with AI"}
								</Button>
							</div>
						</div>
						<RedWordTextarea
							placeholder="Type your task description here."
							id="message"
							onChange={handleDescriptionChange}
							textareaRef={textareaRef}
							value={formValues.description}
							shouldHighlight={shouldHighlightRedWords}
							className={cn(
								darkMode ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]" : "",
								emptyFields.has("description") &&
									darkMode &&
									"border-red-700 bg-red-900/40",
								emptyFields.has("description") &&
									!darkMode &&
									"border-red-500 bg-red-100",
							)}
							style={{ minHeight: "100px" }}
						/>
					</div>
				</div>
				<DialogFooter className="sm:justify-between">
					{/* Left side - Duplicate button (icon only) */}
					{onDuplicate && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleDuplicate}
										disabled={isDuplicating}
										className="h-10 w-10 border-[hsl(var(--brand-alt))] bg-transparent text-[hsl(var(--brand-alt))] hover:bg-[hsl(var(--brand-alt))]/10"
									>
										<Copy className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Duplicate</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}

					{/* Right side - Save buttons (horizontal) */}
					<div className="flex flex-row gap-2">
						<Button
							type="submit"
							variant="outline"
							onClick={() => validateAndSave(true)}
							className="border-[hsl(var(--brand-alt))] bg-transparent text-[hsl(var(--brand-alt))] hover:bg-[hsl(var(--brand-alt))]/10"
						>
							Save and export
						</Button>
						<Button
							type="submit"
							onClick={() => validateAndSave(false)}
							className="border-[hsl(var(--brand-alt))] bg-[hsl(var(--brand-alt))] text-white hover:bg-[hsl(var(--brand-alt-hover))]"
						>
							Save changes
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>

			<RedWordConfirmationDialog
				open={showRedWordDialog}
				onOpenChange={setShowRedWordDialog}
				redWordMatches={pendingSave?.redWordMatches || []}
				onConfirm={handleConfirmWithRedWords}
				onCancel={() => {
					setShowRedWordDialog(false);
					setPendingSave(null);
					setIsFixingRedWords(false);
				}}
				onFixForMe={handleFixRedWords}
				originalDescription={formValues.description}
				darkMode={darkMode}
				action="save"
				isFixing={isFixingRedWords}
			/>
		</Dialog>
	);
}
